// Full-document shell for sealy pages rendered entirely through React.
// reactivated's renderer injects the bundle CSS/JS + __PRELOADED_PROPS__, so we
// deliberately emit none of that here.
import React from "react";
import { useStore } from "react-redux";
import { ErrorBoundary } from "tsi-common-react/src/components/ErrorBoundary/index";
import { IovationScripts } from "tsi-common-react/src/components/IovationScripts/index";
import { FlashMessages } from "tsi-common-react/src/components/Message/index";
import { MetaTags } from "tsi-common-react/src/components/MetaTags/index";
import {
    initPromoCalloutMessages,
    initTokenReplace,
    useFocusFirstFormError,
    useLinkRewrites,
    useRichTextDecorators,
} from "tsi-common-react/src/utils/richTextDecorators";
import { t } from "ttag";

import { Context, templates } from "@reactivated";

import { getDocument, getWindow } from "@thelabnyc/thelabui/src/utils/dom";

import { IReduxState } from "../../store/reducers.interfaces";
import { Template as FinancingModal } from "../../templates/FinancingModalTemplate";
import { Template as SessionKeepAlive } from "../../templates/SessionKeepAliveTemplate";
import { SiteFooter } from "../SiteFooter";
import { SiteHeader } from "../SiteHeader";

type IMessage =
    templates.UtilityPageTemplate["page_chrome"]["messages"][number];

// Server-rendered Django flash messages, rendered + dismissed by the shared
// FlashMessages component (which owns per-item dismissal). This wraps it with
// the sealy close icon and the no-JS fallback banner.
const Messages = ({
    messages,
}: {
    messages: readonly IMessage[];
}): React.JSX.Element => (
    <>
        {messages.length > 0 && (
            <FlashMessages
                closeIcon={
                    <svg
                        className=""
                        role="img"
                        xmlns="http://www.w3.org/2000/svg"
                        xmlSpace="preserve"
                        viewBox="0 0 20 20"
                    >
                        <title>Close</title>
                        <path d="M10 0C4.5 0 0 4.5 0 10s4.5 10 10 10 10-4.5 10-10S15.5 0 10 0m5 13.8L13.8 15 10 11.2 6.2 15 5 13.8 8.8 10 5 6.2 6.2 5 10 8.8 13.8 5 15 6.2 11.2 10z" />
                    </svg>
                }
                messages={messages.map((message) => ({
                    tags: message.tags,
                    content: (
                        <p>
                            <span
                                className={`messages__item__category messages__item__category--${message.level_tag}`}
                            >
                                {message.category_label}
                            </span>{" "}
                            {message.is_html ? (
                                // Only messages explicitly mark_safe'd
                                // server-side render as HTML; plain
                                // messages are auto-escaped.
                                <span
                                    dangerouslySetInnerHTML={{
                                        __html: message.text,
                                    }}
                                />
                            ) : (
                                <span>{message.text}</span>
                            )}
                        </p>
                    ),
                }))}
            />
        )}
        <noscript
            dangerouslySetInnerHTML={{
                __html: `<aside class="messages l-full-width"><ul class="messages__list"><li class="messages__item error"><div class="messages__item__content l-capped-width"><p><span class="messages__item__category messages__item__category--error">${t`Error`}</span>${t`Please enable Javascript`}</p></div></li></ul></aside>`,
            }}
        />
    </>
);

// Google Customer Reviews badge, DNT-gated like its {% jstracker %} wrapper.
// The renderBadge callback itself is defined in the site's legacy.js bundle;
// this only loads platform.js?onload=renderBadge and does NOT inline a
// renderBadge defn.
const GCRBadge = (): React.JSX.Element => (
    <script
        src="https://apis.google.com/js/platform.js?onload=renderBadge"
        async
        defer
    />
);

// Relocates #onetrust-consent-sdk (the banner / preference-center container
// OneTrust's async SDK injects into <body>) to sit just before the body-level
// ancestor of the skip-to-main anchor, for A11y skip-link ordering. In a
// full-document render React OWNS <body> and hydrates its children
// positionally, so doing this move pre-hydration races hydration: if it lands
// #onetrust-consent-sdk before #content before hydration commits, React finds
// it where it expects #content and the whole tree mismatches (observed
// intermittently). So we run it AFTER hydration, from a PageSkeleton effect --
// OneTrust injects the SDK at the end of <body> (React 19 cleanly skips that
// trailing foreign node), then we move it into place once React has
// committed. Idempotent + observer-driven because the SDK appears
// asynchronously and may re-render.
const relocateOneTrustConsentSDK = (): void => {
    const doc = getDocument();
    const win = getWindow();
    const body = doc?.body;
    if (!doc || !win || !body) {
        return;
    }
    const bodyLevelAnchor = (): Node | null => {
        const skip =
            doc.getElementById("skip-to-main") ||
            doc.querySelector('a[href="#main-content"]');
        if (!skip) {
            return null;
        }
        let node: Node | null = skip;
        while (node && node.parentNode !== body) {
            node = node.parentNode;
        }
        return node;
    };
    const move = (): boolean => {
        const el = doc.getElementById("onetrust-consent-sdk");
        if (!el || el.parentNode !== body) {
            return false;
        }
        const anchor = bodyLevelAnchor();
        if (!anchor) {
            return false;
        }
        if (el.nextSibling === anchor) {
            return false;
        }
        body.insertBefore(el, anchor);
        return true;
    };
    if (move()) {
        return;
    }
    let obs: MutationObserver | null = null;
    const tryMove = (): void => {
        if (move() && obs) {
            obs.disconnect();
        }
    };
    obs = new MutationObserver(tryMove);
    obs.observe(body, { childList: true });
    win.addEventListener("load", tryMove);
    win.setTimeout(tryMove, 2000);
    win.setTimeout(tryMove, 5000);
    win.setTimeout(() => {
        move();
        obs?.disconnect();
    }, 10000);
};

// Mutable reactivated props (not Immutable<>): PageSkeleton renders the
// existing in-tree SiteHeader, which forwards into the shared RichNavigation
// component -- both are typed with mutable reactivated props. This matches the
// established SiteHeader (client/components/SiteHeader/) precedent for
// header-integrating entry components. The deeply-readonly StreamField body is
// handled in the body
// renderer.
//
// Per-page chrome (title, meta, canonical, JSON-LD, body_class, flash
// messages) is carried as the `page_chrome` field on each full-page
// @template. The global document shell (head/body globals, header, footer)
// comes from the `chrome` context processor (useContext below), not from
// these props. Typing `pageChrome` to the shared PageChrome (via
// UtilityPageTemplate) lets every full-document page template reuse
// PageSkeleton regardless of their other props.
type IProps = {
    pageChrome: templates.UtilityPageTemplate["page_chrome"];
    children?: React.ReactNode;
    // Header/footer slots. A node renders in place of the default
    // header/<SiteFooter>; `false` omits the slot entirely; `undefined`
    // (default) renders the site chrome, so existing full-document pages are
    // unaffected.
    header?: React.ReactNode | false;
    footer?: React.ReactNode | false;
    // Messages slot. `false` omits the flash-messages block entirely
    // (including its noscript fallback), matching legacy pages whose blanked
    // {% block header %} suppressed the _messages.html include (BasicPage);
    // `undefined` (default) renders it. Pair with build_page_chrome's
    // include_messages=False so the flash queue is not drained either.
    messages?: false;
};

export const PageSkeleton = (props: IProps): React.JSX.Element => {
    const { pageChrome, children, header, footer, messages } = props;
    // Global chrome (header/nav, footer, head/body globals) comes from the
    // `chrome` context processor. PageSkeleton is always the full-document root
    // inside the reactivated Provider, so the context is populated in both SSR
    // and client hydration. This read is intentionally unguarded: callers
    // rendering outside a chrome-carrying Provider (stories/tests) must supply
    // a chrome fixture in their Context value.
    const context = React.useContext(Context);
    const chrome = context.chrome;
    const reduxStore = useStore<IReduxState>();
    // Post-hydration DOM decorators shared by every full-document page: fill CMS
    // rich-text promo tokens, swap the value-prop review-count token for the
    // summed configurator review count, apply the link-rewrite ruleset, and
    // focus the first form error.
    useRichTextDecorators([
        initPromoCalloutMessages,
        () =>
            initTokenReplace(".value-prop", /REVIEW_COUNT_PLACEHOLDER/, () => {
                const { rootProducts } =
                    reduxStore.getState().configurator.entities;
                const reviewCount = rootProducts.reduce(
                    (sum, product) => sum + product.num_reviews,
                    0,
                );
                return `<br/>${reviewCount}`;
            }),
    ]);
    useLinkRewrites();
    useFocusFirstFormError();
    // On a full-document React page React owns <html className="no-js"> (rendered
    // for the no-JS fallback) and `data-useragent` drives the unsupported-browser
    // CSS (html[data-useragent*="MSIE 10.0"], etc.). Both are per-user / JS-gated
    // and these pages are cacheable, so neither can be server-rendered, and an
    // early imperative mutation would race hydration and mismatch on the
    // React-owned <html>. Apply both here, after hydration commits: React owns
    // <html> but never carries `data-useragent` in its vdom and won't re-render
    // <html> (its props don't change), so removing `no-js` sticks. index.tsx
    // still drops `no-js` eagerly on island pages (no full-document hydration to
    // race); legacy.js still sets `data-useragent` for old browsers that never
    // boot React (see legacy.tsx's module-support guard).
    React.useEffect(() => {
        const win = getWindow();
        const doc = getDocument();
        if (doc?.documentElement) {
            doc.documentElement.classList.remove("no-js");
            if (win?.navigator) {
                doc.documentElement.setAttribute(
                    "data-useragent",
                    win.navigator.userAgent,
                );
            }
        }
    }, []);
    // Relocate OneTrust's consent SDK for A11y ordering AFTER hydration -- an
    // inline (pre-hydration) move races React's <body> hydration and mismatches
    // (#content vs the moved #onetrust-consent-sdk). See relocateOneTrustConsentSDK.
    React.useEffect(() => {
        relocateOneTrustConsentSDK();
    }, []);
    // Page-level layout writers: the dynamic CSS custom properties for the
    // configurator upsell-modal offset and the product-hero sticky offset, plus
    // the mobile sticky-header-on-scroll toggle. All read/write the live DOM, so
    // they run client-only after hydration (the properties fall back to their
    // SCSS defaults until then).
    const pageType = pageChrome.page_type;
    const viewName = chrome.view_name;
    React.useEffect(() => {
        const win = getWindow();
        const doc = getDocument();
        if (!win || !doc) {
            return;
        }
        const root = doc.documentElement;
        const mainContent = doc.getElementById("main-content");
        // The upsell info-modal panel tracks the top of #main-content (clamped
        // to 0 once scrolled past it); recomputed on every scroll.
        const setUpsellPanelOffset = () => {
            if (!mainContent) {
                return;
            }
            const top = mainContent.getBoundingClientRect().top;
            root.style.setProperty(
                "--configurator--upsell--panel-offset",
                top < 0 ? "0px" : `${top}px`,
            );
        };
        // One-shot product-hero sticky offset: the top of #main-content.
        if (mainContent) {
            root.style.setProperty(
                "--product-hero--sticky-offset",
                `${mainContent.getBoundingClientRect().top}px`,
            );
        }
        setUpsellPanelOffset();
        // Mobile sticky-header-on-scroll: add/remove the `sticky` class on the
        // header by scroll direction once scrolled past its height. Suppressed on
        // checkout and the product-compare page, matching the legacy
        // view_name / page_type gates.
        const stickyNavEnabled =
            viewName !== "checkout:index" && pageType !== "ProductComparePage";
        const header = doc.querySelector<HTMLElement>("header");
        let lastScrollTop = 0;
        const onScroll = () => {
            setUpsellPanelOffset();
            if (stickyNavEnabled && header) {
                const st = win.scrollY;
                if (
                    st > header.getBoundingClientRect().height &&
                    st > lastScrollTop
                ) {
                    header.classList.add("sticky");
                } else {
                    header.classList.remove("sticky");
                }
                lastScrollTop = st <= 0 ? 0 : st;
            }
        };
        win.addEventListener("scroll", onScroll);
        return () => {
            win.removeEventListener("scroll", onScroll);
        };
    }, [pageType, viewName]);
    return (
        <html className="no-js" lang={chrome.html_lang}>
            <head>
                {/*
                 * sealy-base.html uses an inline `html{visibility:hidden;opacity:0}`
                 * FOUC guard, relying on the bundle CSS (`html{visibility:visible}`)
                 * overriding it by source order. In a full-document React render,
                 * React 19 hoists the bundle stylesheet to the TOP of <head> (before
                 * any inline <style>), which would invert that order and leave the
                 * page permanently hidden. The bundle <link> is render-blocking in
                 * <head> here, so there is no FOUC to guard against -- we
                 * intentionally omit the inline hide.
                 */}
                <meta
                    httpEquiv="content-type"
                    content="text/html; charset=UTF-8"
                />
                <meta name="created" content={chrome.created} />
                <meta
                    name="viewport"
                    content="width=device-width, initial-scale=1.0"
                />
                <title>{pageChrome.title}</title>
                {pageChrome.search_description && (
                    <meta
                        name="description"
                        content={pageChrome.search_description}
                    />
                )}
                {pageChrome.search_keywords && (
                    <meta
                        name="keywords"
                        content={pageChrome.search_keywords}
                    />
                )}
                {pageChrome.robots_noindex && (
                    <meta name="robots" content="noindex" />
                )}
                <link rel="canonical" href={pageChrome.canonical_url} />
                {/* Per-page social-share images ({% block extra_meta %} on the
                    pages that set them); no tag is rendered when empty. */}
                {pageChrome.twitter_image && (
                    <meta
                        name="twitter:image"
                        content={pageChrome.twitter_image}
                    />
                )}
                {pageChrome.og_image && (
                    <meta property="og:image" content={pageChrome.og_image} />
                )}
                <link
                    rel="shortcut icon"
                    type="image/x-icon"
                    href={chrome.favicon_url}
                />
                {/* Per-page stylesheet bundles ({% include_css %} additions in
                    {% block style %} on the pages that set them); rendered
                    after the reactivated bundle CSS so they win the cascade,
                    like the legacy {{ block.super }} ordering. Empty on most
                    pages, which then emit no tag. */}
                {pageChrome.extra_stylesheets.map((href) => (
                    <link
                        key={href}
                        rel="stylesheet"
                        type="text/css"
                        href={href}
                    />
                ))}
                {/* JSON-LD structured data ({% google_page_schema %}) is rendered
                    at the top of <body> -- see the head comment above and the
                    body comment. */}
                <meta httpEquiv="x-ua-compatible" content="ie=edge" />
                {/* sealy-base.html emits a second viewport + empty description */}
                <meta name="description" content="" />
                <meta
                    name="viewport"
                    content="width=device-width, initial-scale=1"
                />
                <link
                    rel="stylesheet"
                    href="https://use.typekit.net/pkh3gvi.css"
                />
                <link
                    rel="stylesheet"
                    href="https://fonts.googleapis.com/css2?family=Open+Sans&display=swap"
                />
                <link rel="preconnect" href="https://cdn.fonts.net" />
                <link
                    rel="stylesheet"
                    href="https://cdn.fonts.net/kit/3ed71a64-0d16-41ee-a9f9-a2bef9e08d96/3ed71a64-0d16-41ee-a9f9-a2bef9e08d96.css"
                />
                {chrome.aws_waf_url && (
                    <script defer={true} src={chrome.aws_waf_url} />
                )}
            </head>
            <body
                id={chrome.body_id}
                className={pageChrome.body_class}
                data-appslug={chrome.app_slug}
                data-version={chrome.version}
                data-environment={chrome.environment}
                data-sentry-dsn={chrome.sentry_dsn}
                data-track-page-type={pageChrome.page_type}
                data-view-name={chrome.view_name}
            >
                {/*
                 * The consent stub, the third-party tags and the JSON-LD render
                 * at the TOP of <body>, not in <head>. React 19 hoists the
                 * <meta> and async <script src> tags up anyway, while an inline
                 * <script> in the React-owned <head> gets paired with whatever a
                 * third party injects there before hydration finishes -- shifting
                 * the whole head by one. OptanonWrapper only has to exist before
                 * the async OneTrust SDK calls it, GTM events queue in dataLayer,
                 * and JSON-LD is crawled anywhere.
                 */}
                <script
                    type="text/javascript"
                    dangerouslySetInnerHTML={{
                        __html: "function OptanonWrapper() { }",
                    }}
                />
                <MetaTags
                    tags={context.meta_tags.meta_tags.value}
                    doNotTrack={chrome.do_not_track}
                />
                {pageChrome.json_ld.map((schema, i) => (
                    <script
                        key={i}
                        type="application/ld+json"
                        dangerouslySetInnerHTML={{ __html: schema }}
                    />
                ))}
                {/* Inlined logo.svg -- the hidden <svg> symbol sheet referenced
                    by <use> across the site. */}
                <div
                    dangerouslySetInnerHTML={{ __html: chrome.logo_svg }}
                    style={{ display: "none" }}
                />
                <div id="content">
                    {messages !== false && (
                        <Messages messages={pageChrome.messages} />
                    )}
                    {header === undefined ? (
                        <header className="header">
                            <SiteHeader {...chrome.site_header} />
                        </header>
                    ) : (
                        header
                    )}
                </div>
                <main role="main" className="main-content">
                    <div id="main-content" />
                    <ErrorBoundary>{children}</ErrorBoundary>
                    {footer === undefined ? (
                        <SiteFooter
                            social={chrome.social}
                            copyrightYear={chrome.copyright_year}
                            footerCopyHtml={chrome.footer_copy_html}
                            footerNavColumns={chrome.footer_nav_columns}
                            privacyPolicyUrl={chrome.privacy_policy_url}
                            termsUrl={chrome.terms_url}
                            staticBase={chrome.static_base}
                            footerPhone={chrome.footer_phone}
                            footerShowContactSupport={
                                chrome.footer_show_contact_support
                            }
                            footerShowNearbyStores={
                                chrome.footer_show_nearby_stores
                            }
                            footerChatOfflineLink={
                                chrome.footer_chat_offline_link
                            }
                            showServiceFooter={chrome.show_service_footer}
                            serviceFooterSnippetsJson={
                                chrome.service_footer_snippets_json
                            }
                            serviceFooterInterval={
                                chrome.service_footer_interval
                            }
                            serviceFooterLoops={chrome.service_footer_loops}
                        />
                    ) : (
                        footer
                    )}
                </main>
                {chrome.show_gcr_badge && <GCRBadge />}
                <UnsupportedWarning staticBase={chrome.static_base} />
                <SessionKeepAlive />
                <FinancingModal />
                {chrome.sticky_menu_html.map((html, i) => (
                    <div key={i} dangerouslySetInnerHTML={{ __html: html }} />
                ))}
                <script
                    defer={true}
                    src={`${chrome.static_base}dist2/legacy.js`}
                />
                {chrome.zendesk_chat_enabled &&
                    pageChrome.chat_widget_enabled &&
                    chrome.zendesk_key && (
                        <script
                            id="ze-snippet"
                            src={`https://static.zdassets.com/ekr/snippet.js?key=${chrome.zendesk_key}`}
                        />
                    )}
                {/* iovation fingerprinting scripts. Injected post-hydration by
                    IovationScripts so the IGLOO config is set before the snare
                    loader runs -- a declarative `<script async src>` snare gets
                    hoisted into React's <head> and can execute before the
                    body-level config on a warm cache, silently degrading
                    fingerprinting. */}
                <IovationScripts
                    iovationUrl={chrome.iovation_url}
                    staticBase={chrome.static_base}
                />
            </body>
        </html>
    );
};

const UnsupportedWarning = ({
    staticBase,
}: {
    staticBase: string;
}): React.JSX.Element => {
    const browsers = [
        {
            title: t`Get Google Chrome`,
            href: "http://www.google.com/chrome",
            img: "tsicommon_cms/img/chrome_32x32.png",
            label: t`Google Chrome`,
        },
        {
            title: t`Get Mozilla Firefox`,
            href: "http://www.firefox.com",
            img: "tsicommon_cms/img/firefox_32x32.png",
            label: t`Mozilla Firefox`,
        },
        {
            title: t`Get Apple Safari`,
            href: "http://www.apple.com/safari/download",
            img: "tsicommon_cms/img/safari_32x32.png",
            label: t`Apple Safari`,
        },
        {
            title: t`Get Microsoft Edge`,
            href: "https://www.microsoft.com/en-us/edge",
            img: "tsicommon_cms/img/edge_32x32.png",
            label: t`Microsoft Edge`,
        },
    ];
    return (
        <>
            <div className="unsupported-browser-container">
                <div className="unsupported-browser">
                    {/* eslint-disable-next-line react/no-unknown-property */}
                    <a
                        className="unsupported-browser__close-btn"
                        href="#"
                        onClick={(e) => {
                            e.preventDefault();
                            const container =
                                e.currentTarget.parentNode?.parentNode;
                            if (container instanceof HTMLElement) {
                                container.style.display = "none";
                            }
                        }}
                    >
                        {t`Close`}
                    </a>
                    <div className="unsupported-browser__title">
                        {t`Improve Your Shopping Experience`}
                    </div>
                    <p className="unsupported-browser__copy">
                        {t`You’re using an unsupported web browser.`}
                        <br />
                        {t`A list of the most popular browsers can be found below. Just click on an icon to visit the download page.`}
                    </p>
                    <ul className="unsupported-browser__list">
                        {browsers.map((b) => (
                            <li
                                className="unsupported-browser__list-item"
                                key={b.img}
                            >
                                <a
                                    title={b.title}
                                    href={b.href}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    <img
                                        src={`${staticBase}${b.img}`}
                                        alt={b.title}
                                        title={b.title}
                                        loading="lazy"
                                        decoding="async"
                                    />
                                    <br />
                                    {b.label}
                                </a>
                            </li>
                        ))}
                    </ul>
                </div>
            </div>
            <div className="unsupported-mobileos-container">
                <div className="unsupported-mobileos">
                    {/* eslint-disable-next-line react/no-unknown-property */}
                    <a
                        className="unsupported-mobileos__close-btn"
                        href="#"
                        onClick={(e) => {
                            e.preventDefault();
                            const container =
                                e.currentTarget.parentNode?.parentNode;
                            if (container instanceof HTMLElement) {
                                container.style.display = "none";
                            }
                        }}
                    >
                        {t`Close`}
                    </a>
                    <div className="unsupported-mobileos__title">
                        {t`Your Software is Out of Date`}
                    </div>
                    <p className="unsupported-mobileos__copy">
                        {t`Your phone or tablet is using software unsupported by our website. To improve your shopping experience, please update your device to the latest version of iOS or Android OS.`}
                    </p>
                </div>
            </div>
        </>
    );
};
